import React, { useEffect, useCallback } from "react";
import {
  TableContainer,
  TableCell,
  Tooltip,
  TableHead,
  IconButton,
  Grid,
  TextField,
  TableBody,
  Table,
  TableRow,
} from "@mui/material";
import { themes, tableHeaderSx, tableRowSx } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { Cancel, AddCircleTwoTone } from "@material-ui/icons";

export const ShiftWorkTable = ({
  rows,
  setRowError,
  setRows,
  rowError,
  onValidateRef,
}) => {
  const validateRows = useCallback(() => {
    const newErrors = rows.map((row, index) => {
      const minEmpty = row.work_min_percentage === "";
      const compEmpty = row.count_as_shift === "";

      let compInvalid = false;
      let minInvalid = false;

      if (!compEmpty && index > 0) {
        const current = parseFloat(row.count_as_shift);
        const previous = parseFloat(rows[index - 1].count_as_shift);
        if (!isNaN(current) && !isNaN(previous)) {
          const isPrevInteger = Number.isInteger(previous);
          if (isPrevInteger) {
            compInvalid = current < previous;
          } else {
            compInvalid = current <= previous;
          }
        } else {
          compInvalid = true;
        }
      }

      if (!minEmpty && index > 0) {
        const currentMin = parseFloat(row.work_min_percentage);
        const prevMin = parseFloat(rows[index - 1].work_min_percentage);
        minInvalid =
          isNaN(currentMin) || isNaN(prevMin) || currentMin <= prevMin;
      }

      return {
        work_min_percentage: minEmpty || minInvalid,
        count_as_shift: compEmpty || compInvalid,
      };
    });

    const hasError = newErrors.some(
      (err) => err.work_min_percentage || err.count_as_shift
    );

    setRowError(newErrors);
    return !hasError;
  }, [rows, setRowError]);

  const handleAddRow = () => {
    const isValid = validateRows();
    if (!isValid) return;

    setRows((prev) => [
      ...prev,
      { work_min_percentage: "", count_as_shift: "" },
    ]);
    setRowError((prev) => [
      ...prev,
      { work_min_percentage: false, count_as_shift: false },
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowError((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);

    const updatedErrors = [...rowError];
    if (!updatedErrors[index])
      updatedErrors[index] = {
        work_min_percentage: false,
        count_as_shift: false,
      };
    updatedErrors[index][field] = false;
    setRowError(updatedErrors);
  };

  useEffect(() => {
    if (onValidateRef) {
      onValidateRef.current = validateRows;
    }
  }, [rows, onValidateRef, validateRows]);

  return (
    <>
      <TableContainer
        sx={{
          width: "100%",
        }}
      >
        <Table className="border">
          <TableHead sx={tableHeaderSx}>
            <TableRow maxWidth="xl" align="left" sx={tableRowSx}>
              <TableCell
                sx={{
                  color: themes.headLine,
                  fontWeight: "bold",
                }}
              >
                Min % Completed
              </TableCell>{" "}
              <TableCell
                sx={{
                  color: themes.headLine,
                  fontWeight: "bold",
                }}
              >
                Count as Shift
              </TableCell>{" "}
              <TableCell
                sx={{
                  color: themes.headLine,
                  fontWeight: "bold",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          {rows.map((row, index) => (
            <TableBody>
              <TableCell>
                <TextField
                  size="small"
                  value={row.work_min_percentage}
                  onChange={(e) => {
                    const val = e.target.value;
                    const validPattern = /^\d{1,3}(\.\d{0,2})?$/;

                    if (val === "" || validPattern.test(val)) {
                      handleChange(index, "work_min_percentage", val);
                    }
                  }}
                  error={rowError?.[index]?.work_min_percentage || false}
                  helperText={
                    rowError[index]?.work_min_percentage
                      ? row.work_min_percentage.trim() === ""
                        ? "Enter percentage"
                        : index > 0 &&
                          parseFloat(row.work_min_percentage) <=
                            parseFloat(rows[index - 1].work_min_percentage)
                        ? `Percentage must be greater than  ${
                            rows[index - 1]?.work_min_percentage
                          }`
                        : ""
                      : ""
                  }
                  placeholder="Enter percentage"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={row.count_as_shift}
                  onChange={(e) => {
                    const val = e.target.value;
                    const validPattern = /^[0-9]?(\.\d{0,2})?$/;
                    if (val === "" || validPattern.test(val)) {
                      handleChange(index, "count_as_shift", val);
                    }
                  }}
                  error={rowError[index]?.count_as_shift}
                  helperText={
                    rowError[index]?.count_as_shift
                      ? row.count_as_shift.trim() === ""
                        ? "Enter Value"
                        : index > 0 &&
                          parseFloat(row.count_as_shift) <=
                            parseFloat(rows[index - 1].count_as_shift)
                        ? `Value must be greater than  ${
                            rows[index - 1]?.count_as_shift
                          }`
                        : ""
                      : ""
                  }
                  placeholder="Enter value"
                />
              </TableCell>
              <TableCell>
                <Tooltip arrow slotProps={tooltipStyleProps} title={"Remove"}>
                  <IconButton
                    onClick={() => handleRemoveRow(index)}
                    disabled={rows?.length === 1}
                  >
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableBody>
          ))}
        </Table>
      </TableContainer>
      <Grid container>
        <Grid item xs={10.75}></Grid>
        <Grid item xs={1}>
          <Tooltip arrow slotProps={tooltipStyleProps} title={"Add Row"}>
            <IconButton onClick={handleAddRow}>
              <AddCircleTwoTone />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};
